import { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { SelectComponentControl } from 'components/_basic/SelectComponent/SelectComponentControl/SelectComponentControl';
import { ISectionRowProps as IProps } from 'components/BusinessToolForm';
import { PRODUCT_SERVICE_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { PRODUCT_SERVICE_OPTIONS_ES } from 'components/BusinessToolForm/constants/es/options_es';
import { PRODUCT_SERVICE_OPTIONS } from 'components/BusinessToolForm/constants/options';
import { PRODUCT_SERVICE_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';

import { concatWithDot } from 'helpers/string';

import { ForecastedConsumptionDataTable } from './ForcastedConsumptionDataTable';
import { FORECASTED_CONSUMPTION_CHART_NAMES, MARKET_ANALYSIS_MAX_LENGTH } from '../../utils/form';
import { UnitsPicker } from '../UnitsPicker';

import styles from './ForecastedConsumption.module.scss';

export const ForecastedConsumption: FC<IProps> = memo(({ name, disabled = false }) => {
  const { control } = useFormContext();
  const { t, i18n } = useTranslation();

  const getChosenOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return PRODUCT_SERVICE_OPTIONS_DEU;

      case 'uk':
        return PRODUCT_SERVICE_OPTIONS_UA;

      case 'es':
        return PRODUCT_SERVICE_OPTIONS_ES;

      default:
        return PRODUCT_SERVICE_OPTIONS;
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextInputControl
          control={control}
          id={concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.TITLE)}
          name={concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.TITLE)}
          label={t('chartTitle')}
          disabled={disabled}
          maxLength={MARKET_ANALYSIS_MAX_LENGTH.ACTUAL_AND_FORECASTED_CONSUMPTION.CHART_TITLE}
          isPreventInactiveScroll
        />
      </Grid>
      <Grid item container justifyContent="space-between">
        <Grid item xs={2} className={styles.selectContainer}>
          <SelectComponentControl
            control={control}
            id={concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.REGION)}
            name={concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.REGION)}
            disabled={disabled}
            options={getChosenOptions(i18n.language)}
            defaultValue={getChosenOptions(i18n.language)[0].value}
          />
        </Grid>
        <Grid container item xs={10} className={styles.formContainer}>
          <UnitsPicker name={concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.UNITS)} disabled={disabled} />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <FormSectionFilledCard>
          <ForecastedConsumptionDataTable name={name} disabled={disabled} />
        </FormSectionFilledCard>
      </Grid>
      <Grid item xs={12}>
        <TextInputControl
          control={control}
          id={concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.SOURCE)}
          name={concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.SOURCE)}
          label={t('dataSource')}
          disabled={disabled}
          maxLength={1000}
        />
      </Grid>
    </Grid>
  );
});
